// Per-user product-tour progress (VRG-228 — E4).
//
// This hook owns the whole persistence policy for tours; ProductTourProvider owns
// the engine and calls into it. Splitting them that way keeps the "which tour is
// running" state machine free of fetch/migration/fallback concerns, and makes the
// three rules below reviewable in one place.
//
//   1. UNKNOWN IS NOT UNSEEN. Until the first fetch settles, the answer to "has
//      this user seen tour X?" is `undefined`, never `false`. Returning `false`
//      while the request is in flight would flash an already-completed tour on
//      every page load — strictly worse than the localStorage behaviour this
//      replaces, because it would happen on EVERY load rather than once per
//      browser.
//   2. LEGACY PROGRESS IS MIGRATED, ONCE. Anyone who dismissed a tour before this
//      shipped must not see it again just because the backing store changed. On
//      the first successful load we push any `vera:tour:<id>:seen` key that has
//      no server row up to the server, as `dismissed` — the honest status, since
//      the old flag recorded that a tour ENDED, not that it was completed.
//   3. LOCALSTORAGE IS THE FALLBACK, NOT THE SOURCE. Once the server has answered
//      we never read localStorage again. If the API FAILS we fall back to reading
//      it, so a network blip degrades to the old per-browser behaviour instead of
//      re-firing every tour forever.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { tourProgressApi, isTerminalTourStatus } from 'services'
import type { TourProgress, TourProgressStatus } from 'services/tourProgressApi'

export const TOUR_PROGRESS_KEY = ['tour-progress']

// The legacy per-browser key, kept verbatim so existing values still resolve.
const legacyKey = (id: string) => `vera:tour:${id}:seen`
const LEGACY_PREFIX = 'vera:tour:'
const LEGACY_SUFFIX = ':seen'

const readLegacySeen = (id: string): boolean => {
  try {
    return window.localStorage.getItem(legacyKey(id)) === '1'
  } catch {
    // Private mode / storage disabled.
    return false
  }
}

const writeLegacySeen = (id: string): void => {
  try {
    window.localStorage.setItem(legacyKey(id), '1')
  } catch {
    /* non-fatal */
  }
}

/** Every tour id this browser has a legacy "seen" flag for. */
const listLegacySeenIds = (): string[] => {
  try {
    const ids: string[] = []
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i)
      if (key && key.startsWith(LEGACY_PREFIX) && key.endsWith(LEGACY_SUFFIX)) {
        if (window.localStorage.getItem(key) === '1') {
          ids.push(key.slice(LEGACY_PREFIX.length, key.length - LEGACY_SUFFIX.length))
        }
      }
    }
    return ids
  } catch {
    return []
  }
}

/** `undefined` = not yet known. Deliberately tri-state; see rule 1 above. */
export type SeenState = boolean | undefined

export interface TourProgressApi {
  /** `undefined` until the first fetch settles (or fails). NEVER assume false. */
  hasSeen: (tourId: string) => SeenState
  /** True once the answer is knowable — from the server, or from the localStorage
   *  fallback after a failed fetch. Auto-start must wait for this. */
  isReady: boolean
  /** True when the server call failed and we are running on localStorage. */
  isDegraded: boolean
  /** The whole recorded row — status AND position. `completed` and `dismissed` are
   *  both "seen" but are not the same achievement, and `in_progress` carries a
   *  `currentStep` worth showing. `undefined` when unknown or running degraded. */
  progressOf: (tourId: string) => TourProgress | undefined
  /** Record a terminal state. Optimistic: the local cache updates immediately so a
   *  re-render cannot double-fire a tour while the PUT is in flight. */
  markSeen: (tourId: string, status?: Extract<TourProgressStatus, 'completed' | 'dismissed'>) => void
  /** Record mid-tour position. Fire-and-forget; failure is not user-visible. */
  markStep: (tourId: string, stepIndex: number) => void
}

export const useTourProgress = (): TourProgressApi => {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: TOUR_PROGRESS_KEY,
    queryFn: () => tourProgressApi.list(),
    // Progress changes only through this app, and every mutation writes the cache
    // directly, so a refetch on every mount is pure noise on a surface that wraps
    // the entire authenticated shell.
    staleTime: 10 * 60 * 1000,
    // One retry, not the default three: while retries are in flight `isReady` is
    // false and no tour can auto-start, so a long retry ladder means "no tours at
    // all" for the user rather than a graceful degrade to localStorage.
    retry: 1,
    // Retries should not be withheld because react-query believes the browser is
    // offline — this query's whole job is to settle, one way or the other.
    // NOTE: setting this was NOT sufficient on its own (see RESOLUTION_TIMEOUT_MS).
    networkMode: 'always',
  })

  // ── The query is not allowed to gate a user-visible behaviour forever ──────
  // Found by deliberately failing the endpoint and watching the client: the query
  // went `fetchStatus: 'paused'`, `status: 'pending'`, and STAYED there. It never
  // reached `isError`, so `isReady` never flipped, so the queued auto-start never
  // settled — tours silently dead, indistinguishable from "this user has no
  // tours". `networkMode: 'always'` did not clear it, so the cause is not simply
  // react-query's offline gate and is not worth reverse-engineering further:
  // relying on a third-party state machine to always reach a terminal state is
  // the actual mistake.
  //
  // So readiness is bounded independently. If progress has not resolved within
  // the timeout we declare ourselves degraded and run on the localStorage
  // fallback. The guarantee this feature needs is narrow and this delivers it:
  // a tour never fires against UNKNOWN state, and "unknown" never lasts forever.
  //
  // 6s: long enough that an ordinarily slow connection still gets real
  // server-side progress (the strictly better answer), short enough that a user
  // who lands on a tour-triggering surface is not left waiting.
  const RESOLUTION_TIMEOUT_MS = 6000
  const [timedOut, setTimedOut] = useState(false)
  useEffect(() => {
    if (query.isSuccess || query.isError) return
    const timer = window.setTimeout(() => setTimedOut(true), RESOLUTION_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [query.isSuccess, query.isError])

  const upsert = useMutation({
    mutationFn: (vars: { tourKey: string; status: TourProgressStatus; currentStep: number }) =>
      tourProgressApi.upsert(vars.tourKey, {
        status: vars.status,
        currentStep: vars.currentStep,
      }),
  })

  const rows = query.data
  // Degraded means "we have NO server answer", not "the last request failed".
  // react-query keeps `data` across a failed BACKGROUND refetch, and throwing
  // away known-good server progress because a refetch blipped would be a
  // regression in the exact scenario the fallback exists to protect — the
  // localStorage mirror is necessarily a subset of it (it only knows what THIS
  // browser did). Fall back only when there is nothing better.
  const isDegraded = (query.isError || timedOut) && !query.data

  const byKey = useMemo(() => {
    const map = new Map<string, TourProgress>()
    for (const row of rows ?? []) map.set(row.tourKey, row)
    return map
  }, [rows])

  // ── Rule 2: one-time migration of legacy localStorage flags ────────────────
  // Runs after the FIRST successful load only. Conditioned on the absence of a
  // server row rather than on a "migrated" marker, which makes it self-limiting:
  // once a row exists the key can never be re-uploaded, so a `completed` cannot
  // be downgraded to `dismissed` by a second pass.
  //
  // MIGRATION IS PART OF READINESS, not something that happens alongside it.
  // This was found by testing, not by reading: with `isReady` tied to the fetch
  // alone, a user whose only record of a tour was the legacy flag saw it fire
  // again on the very load that was supposed to migrate them. The fetch resolves
  // to `[]`, `isReady` flips in that same render, and the queued auto-start reads
  // "no row, therefore unseen" one render BEFORE the migration's local write
  // lands. Which is the whole failure this feature exists to prevent, reproduced
  // exactly once per user — the one time it is least forgivable, since it is the
  // upgrade itself that would have shown them a tour they had already dismissed.
  // So `migrationSettled` gates readiness, and the answer stays `undefined`
  // until the legacy flags have been folded in.
  const migrationRun = useRef(false)
  const [migrationSettled, setMigrationSettled] = useState(false)
  useEffect(() => {
    if (migrationRun.current || !query.isSuccess || !rows) return
    migrationRun.current = true

    const pending = listLegacySeenIds().filter(id => !byKey.has(id))
    if (pending.length === 0) {
      setMigrationSettled(true)
      return
    }

    Promise.allSettled(
      pending.map(id => tourProgressApi.upsert(id, { status: 'dismissed', currentStep: 0 })),
    ).then(() => {
      queryClient.invalidateQueries({ queryKey: TOUR_PROGRESS_KEY })
    })

    // Reflect it locally FIRST, then declare readiness. Both land in the same
    // commit, so the render that first reports ready already has the rows.
    queryClient.setQueryData<TourProgress[]>(TOUR_PROGRESS_KEY, current => [
      ...(current ?? []),
      ...pending.map(id => ({
        tourKey: id,
        status: 'dismissed' as const,
        currentStep: 0,
        completedAt: null,
        updatedAt: new Date().toISOString(),
      })),
    ])
    setMigrationSettled(true)
  }, [query.isSuccess, rows, byKey, queryClient])

  const hasSeen = useCallback(
    (tourId: string): SeenState => {
      // Rule 3: fall back to the old per-browser flag when the server is
      // unreachable. Degrading to "tours fire at most once per browser" is much
      // better than degrading to "tours fire on every load".
      if (isDegraded) return readLegacySeen(tourId)
      // Rule 1: in flight, or migrated flags not yet folded in — the honest
      // answer is "don't know". Answering `false` here is the bug described above.
      if (!rows || !migrationSettled) return undefined
      const row = byKey.get(tourId)
      return row ? isTerminalTourStatus(row.status) : false
    },
    [byKey, rows, isDegraded, migrationSettled],
  )

  /**
   * The recorded status, where `hasSeen` only answers the yes/no.
   *
   * Needed because `completed` and `dismissed` are BOTH "seen" — that is what
   * `isTerminalTourStatus` collapses them into, correctly, for the purpose of never
   * re-offering a tour. But they are not the same achievement, and anything that
   * reports on or rewards completion has to tell them apart: skipping on step 1 is
   * not finishing.
   *
   * Degraded mode returns `undefined` rather than guessing. The legacy per-browser
   * flag recorded only that a tour ENDED, so it cannot distinguish the two, and
   * inventing `completed` from it would credit people for tours they skipped.
   */
  const progressOf = useCallback(
    (tourId: string): TourProgress | undefined => {
      if (isDegraded) return undefined
      if (!rows || !migrationSettled) return undefined
      return byKey.get(tourId)
    },
    [byKey, rows, isDegraded, migrationSettled],
  )

  const writeLocal = useCallback(
    (tourKey: string, status: TourProgressStatus, currentStep: number) => {
      queryClient.setQueryData<TourProgress[]>(TOUR_PROGRESS_KEY, current => {
        const next = [...(current ?? [])]
        const at = next.findIndex(row => row.tourKey === tourKey)
        const row: TourProgress = {
          tourKey,
          status,
          currentStep,
          completedAt: status === 'completed' ? new Date().toISOString() : null,
          updatedAt: new Date().toISOString(),
        }
        if (at >= 0) next[at] = row
        else next.push(row)
        return next
      })
    },
    [queryClient],
  )

  // ── NO WRITE MAY BE BASED ON UNKNOWN PRIOR STATE ──────────────────────────
  // Every write here is a read-modify-write: it needs the current row to decide
  // whether it is an upgrade, and what step to carry forward. Performing one
  // before the fetch lands means reading "no row" and writing as if the tour were
  // brand new — which OVERWRITES whatever the server actually had.
  //
  // Caught live, and it is not a corner case: deep-linking `?tour=x` starts a
  // tour at mount, its `start` event fires a step write immediately, and a row
  // that the server had as `completed` came back as `in_progress`, step 0,
  // `completed_at` NULL. A user replaying a tour they had finished silently
  // destroyed their own completion record. `startTourOnce` is safe because it
  // waits for readiness; the deep-link and Help-menu paths deliberately do not,
  // because a tour someone explicitly asked for should start at once.
  //
  // So writes made before progress is known are BUFFERED, not dropped and not
  // guessed at, and flushed against real state once it arrives.
  const progressKnown = Boolean(rows) || isDegraded

  type PendingWrite = {terminal?: 'completed' | 'dismissed'; step?: number}
  const pendingWrites = useRef(new Map<string, PendingWrite>())

  // Apply a write against KNOWN state.
  const applyWrite = useCallback(
    (tourKey: string, pending: PendingWrite) => {
      const existing = byKey.get(tourKey)

      // COMPLETION IS NEVER DOWNGRADED. `completed` is a strictly stronger claim
      // than `dismissed`, and once made it must survive — skipping a REPLAY is
      // not un-completing. Without this, replaying a finished tour and pressing
      // Esc rewrote the row to `dismissed` and NULLed `completed_at`, destroying
      // the only answer to "how many people finished this tour?", which is half
      // the reason progress moved server-side at all. A completed tour's position
      // is settled too, so a replay's step writes are dropped rather than
      // rewinding `current_step` to 0.
      if (existing?.status === 'completed' && pending.terminal !== 'completed') return

      // Carry the existing status forward when this write does not set one: an
      // auto-started tour is marked terminal the instant it opens so it cannot
      // fire twice, and a step write must not undo that and make it eligible to
      // auto-fire again if the user closes the tab mid-tour. A tour with no row
      // yet — a Help-menu start of something never seen — is genuinely
      // `in_progress`. Likewise keep the last known step for a terminal write:
      // where someone abandoned a tour is the interesting part of a `dismissed`.
      const status: TourProgressStatus = pending.terminal ?? existing?.status ?? 'in_progress'
      const currentStep = pending.step ?? existing?.currentStep ?? 0

      writeLocal(tourKey, status, currentStep)
      upsert.mutate({tourKey, status, currentStep})
    },
    [byKey, upsert, writeLocal],
  )

  // Buffer while unknown, apply once known. Buffered writes for one tour merge:
  // a completion always wins over a dismissal, and the furthest step wins.
  const write = useCallback(
    (tourKey: string, pending: PendingWrite) => {
      if (progressKnown) {
        applyWrite(tourKey, pending)
        return
      }
      const held = pendingWrites.current.get(tourKey) ?? {}
      pendingWrites.current.set(tourKey, {
        terminal: pending.terminal === 'completed' || held.terminal === 'completed'
          ? 'completed'
          : (pending.terminal ?? held.terminal),
        step: Math.max(pending.step ?? -1, held.step ?? -1) >= 0
          ? Math.max(pending.step ?? -1, held.step ?? -1)
          : undefined,
      })
    },
    [progressKnown, applyWrite],
  )

  useEffect(() => {
    if (!progressKnown || pendingWrites.current.size === 0) return
    const buffered = Array.from(pendingWrites.current.entries())
    pendingWrites.current.clear()
    for (const [tourKey, pending] of buffered) applyWrite(tourKey, pending)
  }, [progressKnown, applyWrite])

  const markSeen = useCallback<TourProgressApi['markSeen']>(
    (tourId, status = 'dismissed') => {
      // Mirror to localStorage unconditionally, and NOT through the buffer — it
      // costs nothing, needs no prior state, and is what makes the degraded path
      // (rule 3) hold: a user who finishes a tour and then loses connectivity
      // still won't see it replay.
      writeLegacySeen(tourId)
      write(tourId, {terminal: status})
    },
    [write],
  )

  const markStep = useCallback<TourProgressApi['markStep']>(
    (tourId, stepIndex) => {
      write(tourId, {step: stepIndex})
    },
    [write],
  )

  return {
    hasSeen,
    progressOf,
    // Ready once the fetch has settled AND legacy flags have been folded in (see
    // the migration block) — or once we have given up waiting. Failure and
    // timeout both make us ready IMMEDIATELY on the localStorage fallback rather
    // than leaving tours disabled indefinitely.
    isReady: (query.isSuccess && migrationSettled) || query.isError || timedOut,
    isDegraded,
    markSeen,
    markStep,
  }
}
